import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import SidebarLink from './SidebarLink';
import config from '../../../config/app.config';
import localStorage from '../../../libs/storageHelper';
import SidebarLinkCustomIcon from './SidebarLinkCustomIcon';
import { routes } from '../../../constants/routes';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import { useGetTriageCounts } from './useGetTriageCounts';
import { useInviteParentUtility } from '../../modals/InviteParent/InviteParentUtility.hooks';
import { useAddToTrackingBoardUtility } from '../../modals/AddToTrackingBoard/AddToTrackingBoardUtility.hooks';
import { useNewAppointmentUtility } from '../../modals/NewAppointmentUtility/NewAppointmentUtility.hooks';

const { STAFF, ADMIN, SUPER_ADMIN, PRIMARY_ADMIN } = config.USER_TYPES;
const { SUPPORT_TICKETS_URL } = config;

function SidebarLinkPetPhotoGallery({ title, route }) {
  const location = useLocation();

  return (
    <NavLink
      to={route}
      state={{ from: location }}
      className={({ isActive }) => (isActive ? 'sidebar__link-active' : '')}
    >
      <li className="sidebar__link custom_sidebar_link">
        <ImageOutlineIcon
          size={24}
          className="sidebar__link-icon sidebar__link-icon--photo-gallery"
          aria-hidden
        />
        <p className="sidebar__link-title">{title}</p>
      </li>
    </NavLink>
  );
}

const routesData = {
  Clients: {
  title: 'Parents & Pets',
  icon: 'contact-details',
  route: '/client-list',
},
  MetricsDashboard: {
    title: 'Metrics Dashboard',
    icon: 'web-design',
    route: '/metrics-dashboard',
  },
  TrackingBoard: {
    title: 'Tracking Board',
    icon: 'ptb',
    route: '/tracking-board',
  },
  AddToTrackingBoard: {
    title: 'Add to Tracking Board',
    icon: 'action-dot',
  },
  InviteParent: {
    title: 'Invite Pet Parent',
    icon: 'action-dot',
  },
  NewAppointment: {
    title: 'New Appointment',
    icon: 'action-dot',
    route: '/appointment',
  },
Appointments: {
  title: 'Appointments',
  icon: 'schedule',
  route: routes.appointments.absolute,
},
PetPhotoGallery: {
  title: 'Pet Photo Gallery',
  route: '/pet-photo-gallery',
  Component: SidebarLinkPetPhotoGallery,
},
AppointmentRequests: {
    title: 'Appointment Requests',
    icon: 'calendar-and-loading',
    route: routes.appointmentRequests,
  },
  ManageClients: {
    title: 'Manage Clients',
    icon: 'user',
    route: routes.manageClients.absolute,
    Component: SidebarLink,
  },
  BroadcastNews: {
    title: 'Broadcast',
    icon: 'broadcast-news',
    route: '/broadcast-news',
  },
  ManageFacilities: {
    title: 'Facilities',
    icon: 'hospital-3',
    route: '/manage-facilities',
  },
  ManageAdmins: {
    title: 'Admins',
    icon: 'admin-settings-male',
    route: '/manage-admins',
  },
  ManageStaff: {
    title: 'Staff',
    icon: 'admin-settings-male',
    route: '/manage-staff',
  },
  // ManageReferring: {
  //   title: 'Referring Doctors',
  //   icon: 'doctor',
  //   route: '/manage-referring',
  // },
  ManageBreeds: {
    title: 'Breeds',
    icon: 'paw',
    route: '/manage-breeds',
    Component: SidebarLink,
  },
  ManageUsers: {
    title: 'Users',
    icon: 'user',
    route: '/manage-users',
    Component: SidebarLink,
  },
  ManageSupport: {
    title: 'Support',
    icon: 'online-support',
    link: SUPPORT_TICKETS_URL,
  },
  RefillRequests: {
    title: 'Refill Requests',
    icon: 'pill-in-circle-arrows',
    route: routes.refillRequests,
  },
  Inquiries: {
    title: 'Inquiries',
    icon: 'ask-question',
    route: routes.inquiries,
  },
  Customize: {
    title: 'Customize',
    icon: 'customize',
    route: routes.customize,
  },
};

const roleRoutes = {
  [STAFF]: [
    'Inquiries',
    'RefillRequests',
    'AppointmentRequests',
    'TrackingBoard',
    'Appointments',
    'PetPhotoGallery',
    'Clients',
    'AddToTrackingBoard',
    'InviteParent',
    'NewAppointment',
  ],
  [ADMIN]: [
    'Inquiries',
    'RefillRequests',
    'AppointmentRequests',
    'TrackingBoard',
    'Appointments',
    'PetPhotoGallery',
    'Clients',
    'ManageStaff',
    'BroadcastNews',
    'AddToTrackingBoard',
    'InviteParent',
    'NewAppointment',
  ],
  [PRIMARY_ADMIN]: [
    'Inquiries',
    'RefillRequests',
    'AppointmentRequests',
    'TrackingBoard',
    'Appointments',
    'PetPhotoGallery',
    'Clients',
    'ManageStaff',
    'BroadcastNews',
    'Customize',
    'AddToTrackingBoard',
    'InviteParent',
    'NewAppointment',
  ],
  [SUPER_ADMIN]: [
    'ManageFacilities',
    'ManageAdmins',
    'ManageUsers',
    'BroadcastNews',
    'ManageStaff',
    'MetricsDashboard',
    'ManageBreeds',
    'ManageSupport',
  ],
};

const BOARDING_HIDDEN_ROUTES = ['RefillRequests', 'AppointmentRequests'];

const ACTION_ROUTE_KEYS = [
  'AddToTrackingBoard',
  'InviteParent',
];

const FACILITY_NAV_GROUPS_VET = [
  ['Inquiries', 'RefillRequests', 'AppointmentRequests'],
  ['TrackingBoard', 'Appointments', 'PetPhotoGallery'],
  ['Clients', 'ManageStaff'],
  ['BroadcastNews'],
  ['Customize'],
];

const FACILITY_NAV_GROUPS_BOARDING = [
  ['TrackingBoard', 'Appointments', 'PetPhotoGallery'],
  ['Inquiries'],
  ['Clients', 'ManageStaff'],
  ['BroadcastNews'],
  ['Customize'],
];

const TRIAGE_BADGE_TOOLTIPS = {
  RefillRequests: (
    <>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__heading-row">
          <span className="lp-info-tooltip__heading-text">
            New Refill Requests
          </span>
        </div>
        <div className="lp-info-tooltip__body">
          <strong>
            The number in the blue circle shows new Refill Requests in the{' '}
            <em>Requested</em> status after submission by Pet Parents using the
            Loyal Pet mobile app.
          </strong>
        </div>
      </div>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__body">
          A Request is no longer included in the total when the Status is
          changed to <em>In the Queue</em>, which automatically updates the Pet
          Parent on their phone with:
          <br />
          (i) a post in the Newsfeed in the Loyal Pet mobile app
          <br />
          (ii) a push notification.
        </div>
      </div>
    </>
  ),
  AppointmentRequests: (
    <>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__heading-row">
          <span className="lp-info-tooltip__heading-text">
            New Appointment Requests
          </span>
        </div>
        <div className="lp-info-tooltip__body">
          <strong>
            The number in the blue circle shows newly received Appointment
            Requests after submission by Pet Parents using the Loyal Pet mobile
            app.
          </strong>
        </div>
      </div>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__body">
          A Request is no longer included in the total after:
          <br />
          (i) the <em>Message</em> link is used to reply to the Pet Parent or
          <br />
          (ii) an Appointment is scheduled.
        </div>
      </div>
    </>
  ),
  Inquiries: (
    <>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__heading-row">
          <span className="lp-info-tooltip__heading-text">New Inquiries</span>
        </div>
        <div className="lp-info-tooltip__body">
          <strong>
            The number in the blue circle shows new Inquiries awaiting an
            initial response after submission by Pet Parents using the Loyal Pet
            mobile app.
          </strong>
        </div>
      </div>
      <div className="lp-info-tooltip__section">
        <div className="lp-info-tooltip__body">
          An Inquiry is no longer included in the total after:
          <br />
          (i) the <em>Message</em> link is used to reply to the Pet Parent or
          <br />
          (ii) the Inquiry is closed.
        </div>
      </div>
    </>
  ),
};

export default function SidebarContent() {
  const { type: userType, isPrimary } = localStorage.getFromStorage('loggedUser').user;
  const userRole = isPrimary && userType === ADMIN ? PRIMARY_ADMIN : userType;
  const { data: services } = useGetFacilityServices();
  const labels = useAppointmentLabels();
  const { openInviteParentUtility } = useInviteParentUtility();
  const { openAddToTrackingBoardUtility } = useAddToTrackingBoardUtility();
  const { openNewAppointmentUtility } = useNewAppointmentUtility();

  const isFacilitySidebar =
    userRole === STAFF || userRole === ADMIN || userRole === PRIMARY_ADMIN;

  const showTriageBadges = isFacilitySidebar;
  const { data: triageCounts } = useGetTriageCounts(showTriageBadges);

  const badgeCountsByRoute = {
    AppointmentRequests: triageCounts?.appointmentRequests || 0,
    RefillRequests: triageCounts?.refillRequests || 0,
    Inquiries: triageCounts?.inquiries || 0,
  };

  const filteredRoutes = roleRoutes[userRole].filter(
    (key) => !services?.isBoardingOnly || !BOARDING_HIDDEN_ROUTES.includes(key),
  );

const labelOverrides = {
  NewAppointment: labels.newAppointment,
  Appointments: labels.appointments,
  TrackingBoard: labels.trackingBoard,
  AddToTrackingBoard: labels.addToTrackingBoard,
};

  const renderRoute = (routeKey) => {
    const { Component: CustomComponent, ...route } = routesData[routeKey];
    const SidebarComponent = CustomComponent || SidebarLinkCustomIcon;
    const title = labelOverrides[routeKey] ?? route.title;

    return (
      <div
        key={routeKey}
        data-add-pet-launcher={
          routeKey === 'AddToTrackingBoard' ? true : undefined
        }
        data-invite-parent-launcher={
          routeKey === 'InviteParent' ? true : undefined
        }
      >
        <SidebarComponent
          {...route}
          title={title}
          badgeCount={badgeCountsByRoute[routeKey]}
          badgeTooltip={TRIAGE_BADGE_TOOLTIPS[routeKey]}
          onClick={
            routeKey === 'NewAppointment'
              ? () => openNewAppointmentUtility()
              : routeKey === 'InviteParent'
                ? () => openInviteParentUtility()
                : routeKey === 'AddToTrackingBoard'
                  ? () => openAddToTrackingBoardUtility()
                  : undefined
          }
        />
      </div>
    );
  };

  const renderSuperAdminRoutes = () => filteredRoutes.map(renderRoute);

  const renderFacilityRoutes = () => {
    const permitted = new Set(filteredRoutes);
    const actionKeys = ACTION_ROUTE_KEYS.filter((key) => permitted.has(key));
    const groupDefs = services?.isBoardingOnly
      ? FACILITY_NAV_GROUPS_BOARDING
      : FACILITY_NAV_GROUPS_VET;
    const navGroups = groupDefs
      .map((group) => group.filter((key) => permitted.has(key)))
      .filter((group) => group.length > 0);

    return (
      <>
        {actionKeys.length > 0 ? (
          <div className="sidebar__action-group">
            {actionKeys.map(renderRoute)}
          </div>
        ) : null}
        {navGroups.map((group) => (
          <div key={group.join('-')} className="sidebar__nav-group">
            {group.map(renderRoute)}
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      className={
        isFacilitySidebar
          ? 'sidebar__content sidebar__content--facility'
          : 'sidebar__content'
      }
    >
      <ul className="sidebar__block">
        <div className="sidebar__block--menu">
          {isFacilitySidebar ? renderFacilityRoutes() : renderSuperAdminRoutes()}
        </div>
      </ul>
    </div>
  );
}
